import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { getImageFile, fallbackImage } from 'utils/stringUtils';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';
import ButtonBase from '@material-ui/core/ButtonBase';
import { setLeavingModal } from 'containers/Main/actions';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import Title from './Title';
import NavBarContext from '../../navBarContext';
import ViewLink from '../ViewLink';
import analyzeClientResource from '../../../../utils/analyzeClientResource';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    padding: props => (props.menu ? '0' : '17px'),
  },
  header: {
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
  },
  lists: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  image: {
    width: 107,
    height: 'auto',
    marginRight: 15,
    maxHeight: '100%',
    objectFit: 'cover',
  },
  listItem: {
    width: 236,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'flex-start',
  },
  clientLogoWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 10,
  },
  clientLogo: {
    maxHeight: 45,
    maxWidth: 120,
  },
  imageWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 60,
    justifyContent: 'center',
  },
}));

function ClientResources() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const locale = useAlgoliaLocale();
  const {
    clientResources,
    clientDetails,
    sendMixpanelEvent,
    clientLanding,
    siteCopy,
  } = useContext(NavBarContext);
  const [userResourcesSiteCopy] = useSiteCopySelector(['user-resources']);
  const clientLogo = _get(clientDetails, ['logoReverse', 'url']);
  const formalShortName = _get(clientDetails, 'context.formalShortName', '');
  const userTerm =
    formalShortName && formalShortName.length <= 12
      ? formalShortName
      : _get(clientLanding, 'userTerm', '') || '';
  const location = useLocation();
  const history = useHistory();

  if (!clientResources) return null;
  // redirect
  const redirect = el => {
    const { article, name, type, exitDisclaimer } = el;

    const { isInternal, url } = analyzeClientResource(el);

    const clientName = clientDetails.name;

    if (!url && !isInternal) history.push('/404');

    if (isInternal) {
      sendMixpanelEvent('Menu Item - Clicked', {
        type: 'Client Exclusives',
        name: article || name,
        resource: url,
      });

      history.push(url);
    }

    if (!isInternal) {
      sendMixpanelEvent('Menu Item - Exited', {
        type: 'Client Exclusives',
        name: article || name,
        page: location.pathname,
        exitPage: url,
        client: clientName,
      });

      const finalUrl = url.includes('http') ? url : `https://${url}`;
      if (!finalUrl.includes('assets.ctfassets.net')) {
        dispatch(
          setLeavingModal({
            visible: true,
            title:
              getAlgoliaLocalizedField(el, 'article', locale) ||
              getAlgoliaLocalizedField(el, 'title', locale) ||
              getAlgoliaLocalizedField(el, 'name', locale),
            url: finalUrl,
            organization: clientName,
            resourceType: type || null,
            disclaimer: exitDisclaimer,
          }),
        );
      } else {
        window.open(finalUrl, '_blank', 'noopener');
      }
    }
  };

  return (
    <div className={classes.root}>
      {clientLogo && (
        <div className={classes.clientLogoWrapper}>
          <img
            src={clientLogo}
            alt="Co-brand Logo"
            className={classes.clientLogo}
          />
        </div>
      )}

      <Typography className={classes.header} color="textPrimary" variant="h6">
        {_get(userResourcesSiteCopy, 'pageCopy.title').replace(
          '<userTerm>',
          userTerm,
        )}
      </Typography>
      <div className={classes.lists}>
        {clientResources.slice(0, 5).map(el => {
          const imageSrc = getImageFile(el);
          const { slug, article, name, title } = el;
          return (
            <ButtonBase onClick={() => redirect(el)} key={slug}>
              <div className={classes.listItem}>
                <div className={classes.imageWrapper}>
                  <img
                    src={imageSrc}
                    alt={article || name || title}
                    className={classes.image}
                    onError={e => {
                      e.target.src = fallbackImage;
                    }}
                  />
                </div>
                <Title title={article || name || title} />
              </div>
            </ButtonBase>
          );
        })}
      </div>
      <ViewLink
        label={_get(siteCopy, [
          'pageCopy',
          'submenu-client-resources',
          'view-all',
        ])}
        link="/user-resources"
      />
    </div>
  );
}

export default ClientResources;
